import { type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import {
  CopyableValue,
  Field,
  FieldGrid,
  Section,
} from '../../../components/common/detailParts';
import {
  StatusBadge,
  TypedStatusBadge,
} from '../../../components/common/StatusBadge';
import type { BadgeVariant } from '../../../constants/statusConfig';
import { formatStageName } from '../../../lib/hubspot-management/formatters';
import { cn } from '../../../lib/utils';
import {
  Callout,
  Chip,
  Empty,
  EmptyState,
  ExpandableRow,
} from '../../../components/common/detailListParts';
import {
  ACTIVITY_KIND_LABEL,
  ACTIVITY_KINDS,
  type ActivityEntry,
  type ActivityKind,
  buildActivity,
  releaseVersion,
  ticketQueuePath,
} from '../dappSignals';
import type { SuperDappTabProps } from '../types';

const INITIAL_ROWS = 60;

/** One dot colour per source so the filter chips double as the legend. */
const KIND_DOT: Record<ActivityKind, string> = {
  release: 'bg-teal-400',
  ticket: 'bg-purple-400',
  alert: 'bg-amber-300',
  email: 'bg-sky-400',
  submission: 'bg-orange-400',
  webhook: 'bg-neutral-400',
  event: 'bg-neutral-600',
};

/** Webhook processing status is a free-form string in the DB, so map loosely. */
const WEBHOOK_STATUS_VARIANT: Record<string, BadgeVariant> = {
  success: 'success',
  processed: 'success',
  failed: 'error',
  error: 'error',
  pending: 'warning',
  skipped: 'default',
};

const RELEASE_MILESTONE_TEXT = {
  uploaded: 'uploaded',
  submitted: 'submitted for review',
  published: 'published',
  rejected: 'rejected',
  failed: 'failed processing',
} as const;

function timeOfDay(timestamp: number) {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function dayLabel(timestamp: number, now = new Date()) {
  const date = new Date(timestamp);
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const dayDiff = Math.floor(
    (startOfToday.getTime() -
      new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()) /
      86_400_000,
  );
  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

function groupByDay(entries: ActivityEntry[]) {
  const groups: Array<{ label: string; entries: ActivityEntry[] }> = [];
  for (const entry of entries) {
    const label = dayLabel(entry.at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.entries.push(entry);
    } else {
      groups.push({ label, entries: [entry] });
    }
  }
  return groups;
}

function templateLabel(templateName: string) {
  return templateName.replace(/([A-Z])/g, ' $1').trim();
}

/** Everything that happened to this dapp, merged and newest first. */
export function ActivityTab({
  data,
  formatDate,
  canViewAuditTrail,
}: SuperDappTabProps) {
  const entries = buildActivity(data, { includeEvents: canViewAuditTrail });
  const [kind, setKind] = useState<ActivityKind | 'all'>('all');
  const [showAll, setShowAll] = useState(false);

  const counts = entries.reduce<Partial<Record<ActivityKind, number>>>(
    (acc, entry) => ({ ...acc, [entry.kind]: (acc[entry.kind] ?? 0) + 1 }),
    {},
  );
  const activeKind = counts[kind as ActivityKind] ? kind : 'all';
  const visible =
    activeKind === 'all'
      ? entries
      : entries.filter((entry) => entry.kind === activeKind);
  const shown = showAll ? visible : visible.slice(0, INITIAL_ROWS);

  return (
    <Section
      title="Activity"
      trailing={
        <span className="text-xs tabular-nums text-neutral-500">
          {visible.length}
        </span>
      }
    >
      <div className="flex flex-wrap gap-1.5">
        <FilterChip
          active={activeKind === 'all'}
          count={entries.length}
          onClick={() => setKind('all')}
        >
          All
        </FilterChip>
        {ACTIVITY_KINDS.filter((candidate) => counts[candidate]).map(
          (candidate) => (
            <FilterChip
              key={candidate}
              active={activeKind === candidate}
              count={counts[candidate] ?? 0}
              dotClassName={KIND_DOT[candidate]}
              onClick={() => setKind(candidate)}
            >
              {ACTIVITY_KIND_LABEL[candidate]}
            </FilterChip>
          ),
        )}
      </div>

      {visible.length === 0 ? (
        <EmptyState message="Nothing recorded yet" />
      ) : (
        <div className="mt-2">
          {groupByDay(shown).map((group) => (
            <div key={group.label}>
              <div className="py-3 text-[10.5px] font-medium uppercase tracking-[0.08em] text-neutral-500">
                {group.label}
              </div>
              <div className="divide-y divide-white/5">
                {group.entries.map((entry) => (
                  <ActivityRow
                    key={entry.id}
                    entry={entry}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          ))}
          {visible.length > shown.length && (
            <div className="pt-4 text-center">
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-neutral-200 transition-colors hover:bg-white/[0.08]"
              >
                Show all {visible.length}
              </button>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

function FilterChip({
  active,
  count,
  dotClassName,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  dotClassName?: string;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-teal-400/40 bg-teal-500/10 text-teal-200'
          : 'border-white/5 bg-white/[0.02] text-neutral-400 hover:bg-white/[0.05] hover:text-neutral-200',
      )}
    >
      {dotClassName && (
        <span
          aria-hidden="true"
          className={cn('size-1.5 rounded-full', dotClassName)}
        />
      )}
      {children}
      <span className="tabular-nums text-neutral-500">{count}</span>
    </button>
  );
}

/** Time column + kind dot, then whatever the source has to say. */
function Row({
  entry,
  title,
  meta,
  trailing,
  children,
}: {
  entry: ActivityEntry;
  title: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 py-3">
      <time
        dateTime={new Date(entry.at).toISOString()}
        className="w-16 shrink-0 pt-px text-xs tabular-nums text-neutral-500"
      >
        {timeOfDay(entry.at)}
      </time>
      <span
        aria-hidden="true"
        className={cn(
          'mt-1.5 size-1.5 shrink-0 rounded-full',
          KIND_DOT[entry.kind],
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white/90">{title}</div>
        {meta && <div className="mt-0.5 text-xs text-neutral-500">{meta}</div>}
        {children}
      </div>
      {trailing && (
        <div className="flex shrink-0 items-center gap-3 text-xs leading-5 text-neutral-500">
          {trailing}
        </div>
      )}
    </div>
  );
}

function ActivityRow({
  entry,
  formatDate,
}: {
  entry: ActivityEntry;
  formatDate: SuperDappTabProps['formatDate'];
}) {
  switch (entry.kind) {
    case 'release': {
      const { release, milestone } = entry;
      const failed = milestone === 'rejected' || milestone === 'failed';
      const reason =
        milestone === 'rejected'
          ? release.rejectionReason
          : milestone === 'failed'
            ? release.processingError
            : null;
      return (
        <Row
          entry={entry}
          title={
            <span className={failed ? 'text-rose-300' : undefined}>
              <span className="font-medium tabular-nums">
                {releaseVersion(release)}
              </span>{' '}
              {RELEASE_MILESTONE_TEXT[milestone]}
            </span>
          }
          meta={
            release.uploadProvider ? `via ${release.uploadProvider}` : undefined
          }
        >
          {reason && (
            <div className="mt-1.5">
              <Callout>{reason}</Callout>
            </div>
          )}
        </Row>
      );
    }
    case 'ticket': {
      const { ticket, milestone } = entry;
      return (
        <Row
          entry={entry}
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span>
                Review ticket{' '}
                <span className="font-mono">#{ticket.hubspotId}</span>{' '}
                {milestone}
              </span>
              {ticket.mappedSubmissionStatus && milestone === 'closed' && (
                <TypedStatusBadge
                  type="submission"
                  status={ticket.mappedSubmissionStatus}
                />
              )}
            </span>
          }
          meta={
            <>
              {ticket.subject && <span>{ticket.subject}</span>}
              {ticket.hsPipelineStage && (
                <span>
                  {ticket.subject ? ' · ' : ''}
                  {formatStageName(ticket.hsPipelineStage)}
                </span>
              )}
              {milestone === 'closed' && ticket.closedLabel && (
                <span> · {ticket.closedLabel}</span>
              )}
            </>
          }
          trailing={
            <Link
              to={ticketQueuePath({ ticketId: ticket.id })}
              className="font-medium text-teal-300 transition-colors hover:text-teal-200"
            >
              Open →
            </Link>
          }
        >
          {milestone === 'closed' && ticket.reviewerNote && (
            <p className="mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-neutral-400">
              {ticket.reviewerNote}
            </p>
          )}
        </Row>
      );
    }
    case 'alert': {
      const { alert } = entry;
      return (
        <Row
          entry={entry}
          title={alert.title}
          meta={[alert.userEmail || 'Publisher', alert.type].join(' · ')}
          trailing={
            <div className="flex flex-col items-end gap-1">
              {/* Alerts share the email delivery vocabulary (Sent/Pending/Failed). */}
              <TypedStatusBadge type="email" status={alert.status} />
              {alert.readAt && (
                <span className="text-[10.5px] text-neutral-600">
                  Read {formatDate(alert.readAt)}
                </span>
              )}
            </div>
          }
        >
          {alert.message && (
            <p className="mt-1.5 text-xs leading-relaxed text-neutral-400">
              {alert.message}
            </p>
          )}
        </Row>
      );
    }
    case 'email': {
      const { email } = entry;
      return (
        <Row
          entry={entry}
          title={
            <ExpandableRow
              summary={
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                    <span className="min-w-0 truncate text-sm text-white/90">
                      {email.subject}
                    </span>
                    <TypedStatusBadge type="email" status={email.status} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 text-xs text-neutral-500">
                    <span className="truncate">{email.recipientEmail}</span>
                    <span aria-hidden="true" className="text-neutral-700">
                      ·
                    </span>
                    <span className="capitalize">
                      {templateLabel(email.templateName)}
                    </span>
                    {email.retryCount > 0 && (
                      <>
                        <span aria-hidden="true" className="text-neutral-700">
                          ·
                        </span>
                        <span className="tabular-nums">
                          {email.retryCount} retries
                        </span>
                      </>
                    )}
                  </div>
                  {email.failureReason && (
                    <Callout label="Error:">{email.failureReason}</Callout>
                  )}
                </div>
              }
            >
              <EmailDetails email={email} formatDate={formatDate} />
            </ExpandableRow>
          }
        />
      );
    }
    case 'submission': {
      const { submission } = entry;
      return (
        <Row
          entry={entry}
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span>HubSpot {submission.submissionType}</span>
              {submission.releaseVersion && (
                <span className="text-xs tabular-nums text-neutral-500">
                  v{submission.releaseVersion}
                </span>
              )}
              {submission.hubspotTicketId && (
                <Chip mono>#{submission.hubspotTicketId}</Chip>
              )}
            </span>
          }
          meta={
            <>
              {submission.responseStatus !== null &&
                `HTTP ${submission.responseStatus}`}
              {submission.retryCount > 0 &&
                `${submission.responseStatus !== null ? ' · ' : ''}${submission.retryCount} retries`}
            </>
          }
          trailing={
            <StatusBadge
              status={submission.succeeded ? 'Success' : 'Failed'}
              variant={submission.succeeded ? 'success' : 'error'}
            />
          }
        >
          {submission.errorMessage && (
            <div className="mt-1.5 text-xs text-rose-300/80">
              {submission.errorMessage}
            </div>
          )}
        </Row>
      );
    }
    case 'webhook': {
      const { log } = entry;
      return (
        <Row
          entry={entry}
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span className="capitalize">{log.provider} webhook</span>
              <span className="font-mono text-xs text-neutral-400">
                {log.eventType}
              </span>
            </span>
          }
          meta={
            log.processedAt
              ? `Processed ${formatDate(log.processedAt)}`
              : undefined
          }
          trailing={
            <>
              {log.signatureValid !== null && (
                <span
                  className={
                    log.signatureValid ? 'text-teal-400' : 'text-rose-400'
                  }
                >
                  {log.signatureValid ? 'Signed' : 'Bad signature'}
                </span>
              )}
              <StatusBadge
                status={log.processingStatus}
                variant={
                  WEBHOOK_STATUS_VARIANT[log.processingStatus.toLowerCase()] ??
                  'default'
                }
              />
            </>
          }
        >
          {log.processingError && (
            <div className="mt-1.5 text-xs text-rose-300/80">
              {log.processingError}
            </div>
          )}
        </Row>
      );
    }
    case 'event': {
      const { event } = entry;
      return (
        <Row
          entry={entry}
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-neutral-500">{event.source}</span>
              <span>
                {event.action} {event.noun}
              </span>
            </span>
          }
          meta={
            <span className="font-mono">
              session {event.sessionId.slice(0, 8)}
            </span>
          }
        />
      );
    }
    default:
      return null;
  }
}

function EmailDetails({
  email,
  formatDate,
}: {
  email: Extract<ActivityEntry, { kind: 'email' }>['email'];
  formatDate: SuperDappTabProps['formatDate'];
}) {
  const related = [
    email.alertId && 'Alert',
    email.kybSubmissionId && 'KYB submission',
    email.releaseId && 'Release',
    email.dappId && 'This dapp',
    email.publisherUserJoinId && 'Publisher',
  ].filter((value): value is string => Boolean(value));

  return (
    <div className="flex flex-col gap-5 border-l border-white/5 pl-4">
      <FieldGrid cols={2}>
        <Field label="Recipient">
          <span className="text-neutral-300">
            {email.recipientName
              ? `${email.recipientName} <${email.recipientEmail}>`
              : email.recipientEmail}
          </span>
        </Field>
        <Field label="Sender">
          <span className="text-neutral-300">
            {email.senderName} &lt;{email.senderEmail}&gt;
          </span>
        </Field>
      </FieldGrid>

      <FieldGrid>
        <Field label="Created">
          <span className="text-neutral-300">{formatDate(email.created)}</span>
        </Field>
        <Field label="Sent">
          <span className="text-neutral-300">{formatDate(email.sentAt)}</span>
        </Field>
        <Field label="Failed">
          <span
            className={email.failedAt ? 'text-rose-300' : 'text-neutral-300'}
          >
            {formatDate(email.failedAt)}
          </span>
        </Field>
        <Field label="Last retry">
          <span className="text-neutral-300">
            {formatDate(email.lastRetryAt)}
          </span>
        </Field>
        <Field label="Brevo status">
          {email.brevoResponseStatus === null ? (
            <Empty />
          ) : (
            <span
              className={
                email.brevoResponseStatus >= 200 &&
                email.brevoResponseStatus < 300
                  ? 'tabular-nums text-teal-400'
                  : 'tabular-nums text-rose-400'
              }
            >
              {email.brevoResponseStatus}
            </span>
          )}
        </Field>
        <Field label="Brevo message ID">
          {email.brevoMessageId ? (
            <CopyableValue value={email.brevoMessageId} />
          ) : (
            <Empty />
          )}
        </Field>
      </FieldGrid>

      {related.length > 0 && (
        <Field label="Related">
          <div className="flex flex-wrap gap-1.5">
            {related.map((entity) => (
              <Chip key={entity}>{entity}</Chip>
            ))}
          </div>
        </Field>
      )}
    </div>
  );
}
